import type { StoreOrderState } from './orders';

/**
 * What one account may spend, and how many charges it may hold open.
 *
 * ## Why a spending cap exists at all
 *
 * This is a closed alpha with real money and no refund tooling. A cap is the
 * difference between a bug costing somebody twenty reais and costing them
 * everything they have; it is also what stops a stolen card being emptied into
 * a game account in an afternoon. R$ 200 in twenty-four hours is generous for
 * the biggest package and small enough that the worst case is survivable.
 *
 * ## A ROLLING window, not a calendar day
 *
 * "Today" needs a timezone, and a timezone is either a value the client sends -
 * which makes the cap a suggestion - or a server guess about where somebody
 * lives. A rolling twenty-four hours needs neither, and it cannot be reset by
 * waiting for midnight somewhere.
 *
 * ## What counts
 *
 * Money that is committed, not money that was refused. An order in flight or
 * already delivered holds its share; one that was rejected, cancelled, expired
 * or refunded releases it. That is what makes an abandoned Pix free the limit
 * again half an hour later without anybody intervening.
 */

/** R$ 200,00, in centavos. Integer, like every other amount in this system. */
export const STORE_DAILY_LIMIT_CENTS = 20_000;

/** Twenty-four hours, measured backwards from the server's own clock. */
export const STORE_LIMIT_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * How many unpaid charges one account may hold at once.
 *
 * Three, because a player who opens a charge, changes their mind and opens
 * another is doing something ordinary, and because every open charge is a QR
 * somebody could still pay. Unbounded would let one account keep hundreds of
 * payable codes alive at the provider.
 */
export const STORE_MAX_PENDING_ORDERS = 3;

/**
 * How many orders an account may create in a minute.
 *
 * A rate limit rather than a spending limit: the two above bound the money and
 * the open charges, this one bounds the requests. Computed from `created_at` in
 * SQL rather than held in memory, so it is the same limit on every replica.
 */
export const STORE_CREATE_RATE_LIMIT = 6;
export const STORE_CREATE_RATE_WINDOW_MS = 60_000;

/** How long a Pix charge stays payable. */
export const STORE_PIX_TTL_MS = 30 * 60 * 1000;

/**
 * The states whose money is still committed.
 *
 * `granted` is included: a delivered purchase spent the limit and does not give
 * it back. `refund_pending` is included because the refund has not been
 * confirmed yet, and releasing the limit on a request that might be refused
 * would let somebody spend twice by asking for their money back.
 */
export const LIMIT_CONSUMING_STATES: readonly StoreOrderState[] = Object.freeze([
  'created',
  'pending',
  'approved',
  'granted',
  'refund_pending',
]);

/**
 * The states that release it.
 *
 * Every one of them is terminal and means no money is ours: refused, abandoned,
 * timed out, or given back. `manual_review` is in NEITHER list on purpose -
 * see {@link consumesLimit}.
 */
export const LIMIT_RELEASING_STATES: readonly StoreOrderState[] = Object.freeze([
  'rejected',
  'cancelled',
  'expired',
  'refunded',
]);

/**
 * Does an order in this state still hold part of the limit?
 *
 * `manual_review` returns TRUE, and deliberately so. An order under review is
 * one where something did not add up, which very often means money arrived that
 * we have not accounted for. Releasing its share would let a divergence become
 * extra spending room; holding it costs the player nothing they can notice and
 * fails in the safe direction.
 */
export function consumesLimit(state: StoreOrderState): boolean {
  return !LIMIT_RELEASING_STATES.includes(state);
}

/** The two states where a charge is open and unpaid. */
export const PENDING_ORDER_STATES: readonly StoreOrderState[] = Object.freeze([
  'created',
  'pending',
]);

export interface StoreLimitStatus {
  /** When the oldest committed order leaves the window. Null when none is. */
  readonly resetsAt: Date | null;
  /** Centavos already committed inside the window. */
  readonly usedCents: number;
  /** What is left. Never negative, even if the cap were lowered under somebody. */
  readonly remainingCents: number;
  readonly limitCents: number;
  /** Open, unpaid charges right now. */
  readonly pendingOrders: number;
  readonly maxPendingOrders: number;
}

export function storeLimitStatus(
  usedCents: number,
  pendingOrders: number,
  resetsAt: Date | null = null,
): StoreLimitStatus {
  return {
    resetsAt,
    usedCents,
    remainingCents: Math.max(0, STORE_DAILY_LIMIT_CENTS - usedCents),
    limitCents: STORE_DAILY_LIMIT_CENTS,
    pendingOrders,
    maxPendingOrders: STORE_MAX_PENDING_ORDERS,
  };
}

/**
 * May this account commit `priceCents` more?
 *
 * Returns the REASON rather than a boolean, because the two refusals are
 * different things a player needs to be told apart: "you have spent your daily
 * limit" and "you have three codes open already, pay or abandon one".
 */
export function limitRefusalFor(
  status: StoreLimitStatus,
  priceCents: number,
): 'daily_limit_reached' | 'too_many_pending_orders' | null {
  if (status.pendingOrders >= status.maxPendingOrders) {
    return 'too_many_pending_orders';
  }
  if (priceCents > status.remainingCents) {
    return 'daily_limit_reached';
  }
  return null;
}

/**
 * Prices as the interface prints them: `R$ 4,90`.
 *
 * Integer arithmetic over centavos, never `toFixed` on a division. The label is
 * derived from the same integer the charge uses, so the two cannot disagree.
 */
export function formatBrl(cents: number): string {
  const negative = cents < 0;
  const absolute = Math.abs(cents);
  const whole = Math.trunc(absolute / 100);
  const fraction = String(absolute % 100).padStart(2, '0');
  // Thousands separated with a dot, decimals with a comma - pt-BR, written out
  // rather than delegated to `Intl`, whose output varies by runtime and ICU
  // build and would make a price label environment-dependent.
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negative ? '-' : ''}R$ ${grouped},${fraction}`;
}

/**
 * What a Cash purchase would do to an outstanding debt.
 *
 * The SAME arithmetic `grantApproved` performs, in one place, so what a card
 * promises and what the transaction does cannot drift. Exported from `shared`
 * rather than written into the service, because the interface shows it and the
 * server computes it - and two implementations of "how much of this lands in
 * the balance" is exactly the kind of pair that disagrees under a refund.
 *
 * Null for a product that delivers no Cash: VIP settles nothing.
 */
export interface CashDebtPreview {
  readonly debtSettled: bigint;
  readonly cashCredited: bigint;
  readonly debtRemaining: bigint;
}

export function previewCashDebt(debt: bigint, cashAmount: bigint): CashDebtPreview | null {
  if (cashAmount <= 0n) {
    return null;
  }
  const settled = debt < cashAmount ? debt : cashAmount;
  return {
    debtSettled: settled,
    cashCredited: cashAmount - settled,
    debtRemaining: debt - settled,
  };
}
